import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Calendar,
  Trash2,
  Circle,
  Globe,
  Monitor,
  Smartphone,
  Tablet,
} from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import ReactCountryFlag from "react-country-flag";

// Add missing type definitions
interface AnalyticsTableViewProps {
  allRecords: any[];
  selectedRecords: Set<string>;
  isDeleting: boolean;
  loadingRecords: boolean;
  pageSize: number;
  hasActiveFilters: boolean;
  formatDuration: (seconds: number) => string;
  formatTimeAgo: (dateString: string) => string;
  onSelectAll: (checked: boolean) => void;
  onSelectRecord: (recordId: string, checked: boolean) => void;
  onDeleteRecord: (recordId: string) => void;
  onClearFilters: () => void;
}

const CountryFlag = ({ countryCode }: { countryCode?: string }) => {
  if (!countryCode) {
    return (
      <div className="flex items-center justify-center w-6 h-4 bg-muted rounded-sm">
        <Globe className="h-3 w-3 text-muted-foreground" />
      </div>
    );
  }

  return (
    <ReactCountryFlag
      countryCode={countryCode.toUpperCase()}
      svg
      style={{
        width: "1.5rem",
        height: "1rem",
        borderRadius: "2px",
      }}
      title={countryCode.toUpperCase()}
    />
  );
};

const DeviceIcon = ({ deviceType }: { deviceType?: string }) => {
  const iconClass = "h-4 w-4";
  switch (deviceType?.toLowerCase()) {
    case "mobile":
      return <Smartphone className={`${iconClass} text-blue-600`} />;
    case "tablet":
      return <Tablet className={`${iconClass} text-green-600`} />;
    case "desktop":
      return <Monitor className={`${iconClass} text-purple-600`} />;
    default:
      return <Monitor className={`${iconClass} text-muted-foreground`} />;
  }
};

export const AnalyticsTableView = ({
  allRecords,
  selectedRecords,
  isDeleting,
  loadingRecords,
  pageSize,
  hasActiveFilters,
  formatDuration,
  formatTimeAgo,
  onSelectAll,
  onSelectRecord,
  onDeleteRecord,
  onClearFilters,
}: AnalyticsTableViewProps) => {
  const { t } = useTranslation("analytics");
  // Helper function to determine if a session is active (last seen within 30 seconds)
  const isSessionActive = (lastSeenAt: string): boolean => {
    const lastSeenTime = new Date(lastSeenAt).getTime();
    const currentTime = new Date().getTime();
    const thirtySecondsInMs = 30 * 1000;
    return currentTime - lastSeenTime <= thirtySecondsInMs;
  };
  if (loadingRecords) {
    return (
      <div className="space-y-3">
        {Array.from({ length: pageSize }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (allRecords.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <Calendar className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="mb-2 text-lg font-semibold">
            {t("analytics:analyticsTableView.empty.title")}
          </h3>
          <p className="text-muted-foreground max-w-sm mx-auto">
            {hasActiveFilters
              ? t("analytics:analyticsTableView.empty.filtered")
              : t("analytics:analyticsTableView.empty.noData")}
          </p>
          {hasActiveFilters && (
            <Button variant="outline" className="mt-4" onClick={onClearFilters}>
              {t("analytics:analyticsTableView.empty.clearFilters")}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="w-12">
              <Checkbox
                checked={
                  allRecords.length > 0 &&
                  selectedRecords.size === allRecords.length
                }
                onCheckedChange={onSelectAll}
                aria-label="Select all sessions"
              />
            </TableHead>
            <TableHead className="font-semibold">
              {t("analytics:analyticsTableView.session")}
            </TableHead>
            <TableHead className="font-semibold">
              {t("analytics:analyticsTableView.location")}
            </TableHead>
            <TableHead className="font-semibold">
              {t("analytics:analyticsTableView.deviceBrowser")}{" "}
            </TableHead>
            <TableHead className="font-semibold">
              {t("analytics:analyticsTableView.duration")}
            </TableHead>
            <TableHead className="font-semibold">
              {t("analytics:analyticsTableView.presence")}
            </TableHead>
            <TableHead className="w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {allRecords.map((record, index) => (
            <TableRow
              key={record.id}
              className={index % 2 === 0 ? "bg-muted/20" : ""}
            >
              <TableCell className="py-4">
                <Checkbox
                  checked={selectedRecords.has(record.id)}
                  onCheckedChange={(checked) =>
                    onSelectRecord(record.id, checked as boolean)
                  }
                  aria-label={`Select session ${record.id}`}
                />
              </TableCell>
              <TableCell className="py-4">
                <div className="space-y-1">
                  <div className="font-medium text-sm">
                    {format(new Date(record.first_seen_at), "MMM dd, yyyy")}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {format(new Date(record.first_seen_at), "HH:mm:ss")}
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {formatTimeAgo(record.first_seen_at)}
                  </Badge>
                </div>
              </TableCell>
              <TableCell className="py-4">
                <div className="flex items-center gap-3">
                  <CountryFlag countryCode={record.country_code} />
                  <div className="space-y-1">
                    <div className="text-sm font-medium">
                      {record.city && record.region
                        ? `${record.city}, ${record.region}`
                        : record.country_code || "Unknown"}
                    </div>
                    {record.country_code && (
                      <div className="text-xs text-muted-foreground uppercase">
                        {record.country_code}
                      </div>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell className="py-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <DeviceIcon deviceType={record.device_type} />
                    <span className="text-sm font-medium capitalize">
                      {record.device_type || "Unknown"}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {record.browser_name || "Unknown Browser"}
                  </div>
                  {record.operating_system && (
                    <div className="text-xs text-muted-foreground">
                      {record.operating_system}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell className="py-4">
                <Badge
                  variant="secondary"
                  className="font-mono font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                >
                  {formatDuration(record.total_duration_seconds)}
                </Badge>
              </TableCell>
              <TableCell className="py-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Circle
                      className={`h-2 w-2 ${
                        isSessionActive(record.last_seen_at)
                          ? "fill-green-500 text-green-500"
                          : "fill-gray-400 text-gray-400"
                      }`}
                    />
                    <Badge
                      variant={
                        isSessionActive(record.last_seen_at)
                          ? "default"
                          : "secondary"
                      }
                      className={`text-xs ${
                        isSessionActive(record.last_seen_at)
                          ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300"
                          : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                      }`}
                    >
                      {isSessionActive(record.last_seen_at)
                        ? "Active"
                        : "Inactive"}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t("analytics:analyticsTableView.lastSeen")}{" "}
                    {formatTimeAgo(record.last_seen_at)}
                  </div>
                </div>
              </TableCell>
              <TableCell className="py-4">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" disabled={isDeleting}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t(
                          "analytics:analyticsTableView.deleteSessionDialog.title"
                        )}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t(
                          "analytics:analyticsTableView.deleteSessionDialog.description"
                        )}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>
                        {t(
                          "analytics:analyticsTableView.deleteSessionDialog.cancel"
                        )}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => onDeleteRecord(record.id)}
                        className="bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60"
                      >
                        {t(
                          "analytics:analyticsTableView.deleteSessionDialog.confirm"
                        )}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
